import RemoveIcon from '@mui/icons-material/Remove';
import {
	Autocomplete,
	IconButton,
	Stack,
	TextField,
	Typography
} from '@mui/material';
import { useCurrentIdP } from 'api/idps';
import { GENERIC_IDP_ID } from 'consts';
import { capitalize } from 'lodash';
import React from 'react';
import { IFGAGroupMapping } from 'types/api';
import { IOption } from 'types/fields';
import { isOptionEqualToValue } from 'utils/form';

interface IProps {
	fgaType: string;
	index: number;
	row: IFGAGroupMapping;
	relationOptions: IOption[];
	resourceOptions: IOption[];
	onEdit: (patch: Partial<IFGAGroupMapping>, persistNow?: boolean) => void;
	onDelete: () => void;
	onPersist: () => void;
}

const FGAMappingItem: React.FC<IProps> = ({
	fgaType,
	index,
	row,
	relationOptions,
	resourceOptions,
	onEdit,
	onDelete,
	onPersist
}) => {
	const { data: idpData } = useCurrentIdP();

	return (
		<Stack
			direction="row"
			data-testid={`fga-mapping-${fgaType}-${index}`}
			gap={2}
			my={1}
		>
			<TextField
				size="small"
				label={intl.tr('ssoConfig.fields.groupsMapping.groupLabel', {
					idpName: idpData?.name,
					isGeneric: idpData?.id === GENERIC_IDP_ID
				})}
				value={row.group}
				onChange={(e) => onEdit({ group: e.target.value })}
				onBlur={onPersist}
				required
				sx={{ flexGrow: 1, flexBasis: 0 }}
				slotProps={{ inputLabel: { shrink: true } }}
			/>
			<Stack alignSelf="center">
				<Typography variant="body2">
					{intl.tr('ssoConfig.fields.mapping.mapTo')}
				</Typography>
			</Stack>
			<Autocomplete<IOption>
				data-testid={`fga-mapping-relation-autocomplete-${fgaType}-${index}`}
				clearIcon={null}
				size="small"
				options={relationOptions}
				value={relationOptions.find((o) => o.value === row.relation) ?? null}
				isOptionEqualToValue={isOptionEqualToValue}
				onChange={(_, newValue) =>
					onEdit({ relation: newValue?.value ?? '' }, true)
				}
				sx={{ flexGrow: 1, flexBasis: 0 }}
				renderInput={(params) => (
					<TextField
						{...params}
						required
						label={intl.tr('ssoConfig.fields.fgaMapping.relationLabel')}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				)}
			/>
			<Autocomplete<IOption>
				data-testid={`fga-mapping-resource-autocomplete-${fgaType}-${index}`}
				clearIcon={null}
				size="small"
				// A type with a single resource leaves nothing to choose once the row holds it. A row
				// still pointing at a removed resource stays open so the admin can pick the valid one.
				disabled={
					resourceOptions.length === 1 &&
					resourceOptions[0].value === row.resource
				}
				options={resourceOptions}
				value={resourceOptions.find((o) => o.value === row.resource) ?? null}
				isOptionEqualToValue={isOptionEqualToValue}
				onChange={(_, newValue) =>
					onEdit({ resource: newValue?.value ?? '' }, true)
				}
				sx={{ flexGrow: 1, flexBasis: 0 }}
				renderInput={(params) => (
					<TextField
						{...params}
						required
						label={capitalize(fgaType)}
						slotProps={{ inputLabel: { shrink: true } }}
					/>
				)}
			/>
			<Stack data-testid={`fga-mapping-${fgaType}-delete-button`}>
				<IconButton
					data-testid={`fga-mapping-${fgaType}-${index}-delete-button`}
					onClick={onDelete}
				>
					<RemoveIcon />
				</IconButton>
			</Stack>
		</Stack>
	);
};

export default FGAMappingItem;
